import React, { PureComponent, ReactNode } from "react"
import { DispatchProp } from "react-redux"
import { connectRedux } from '../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../helpers/withRouter'
import { ITown } from "../../models"
import { IStoreState } from "../../store/states"
import { Application } from "../../config"
import { Button, MenuItem, Modal, Spinner } from "../../components"
import { LiteralsService, UserService } from "../../services"
import { urls } from "../../routing"
import StyledDiv from './styles'
import { Dispatch, AnyAction} from 'redux'
import { fetchAreasAction, fetchClientInfoAction, setAreaChangedAction, setNotificationAction, setSelectedAreaAction, setUserInfoAction } from "../../store/actions"

interface IProps extends DispatchProp<any>, RouteComponentProps {
  towns: ITown[]
  selectedTownId: number | null
  isExperienced: boolean
  userTownId: number
  isLoading: boolean
  documentationRequired: boolean
  isFetchingAreas: boolean
  isGuest: boolean
}

const { isNemi } = Application

class TownsModal extends PureComponent<IProps> {

  private get modalContent(): ReactNode {
    const { towns, selectedTownId, isFetchingAreas } = this.props

    const townsItems = towns.sort((a, b) => a.name.localeCompare(b.name)).map(town => (
      <section key={town.id} className="row">
        <MenuItem
          key={town.id}
          label={town.name}
          onClick={ () => this.handleAreaSelection(town.id) }
          icon={ selectedTownId === town.id ? 'checkLanguage' : undefined }
        />
      </section>
    ))

    return (
      <StyledDiv>
        <h3 className="modal-title"> { LiteralsService.get('chooseArea', true) } </h3>
        <section className="towns">
          { isFetchingAreas ? <Spinner/> : townsItems }
        </section>
        <footer>
          <Button
            title={ LiteralsService.get('save', true) }
            onPress={ this.handleSave }
            className={ 'saveButton' }
            isDisabled={ !selectedTownId || isFetchingAreas }
            filled
            upper
          />
        </footer>
      </StyledDiv>
    )
  }

  // Guest mode has no user profile to store the town in, so the choice travels
  // in the URL as ?townId=N and the modal only asks for it while it is missing.
  private get urlTownId(): string | null {
    return new URLSearchParams(this.props.location.search).get('townId')
  }

  private get isGuestSelectionPending(): boolean {
    const { isGuest, location } = this.props

    return isGuest &&
      location.pathname.startsWith(urls.newReservationBusStopsStep) &&
      !this.urlTownId
  }

  public componentDidMount(): void {
    this.fetchGuestAreas()
  }

  public componentDidUpdate(): void {
    this.fetchGuestAreas()
  }

  public render(): ReactNode {
    const { towns, isExperienced, documentationRequired, isGuest, isFetchingAreas } = this.props

    if (isGuest) {
      if (!this.isGuestSelectionPending || (!towns.length && !isFetchingAreas)) return null
      return (
        <Modal isHidden={ false }>
          { this.modalContent }
        </Modal>
      )
    }

    if ( isExperienced || !towns.length || !isNemi || documentationRequired) return null
    return (
      <Modal isHidden={ false }>
        { this.modalContent }
      </Modal>
    )
  }

  private fetchGuestAreas(): void {
    const { towns, isFetchingAreas, dispatch } = this.props

    if (this.isGuestSelectionPending && !towns.length && !isFetchingAreas) {
      dispatch(fetchAreasAction())
    }
  }

  private handleAreaSelection(townId: number): void {
    this.props.dispatch(setSelectedAreaAction(townId))
  }

  private handleSave = (): void => {
    const { selectedTownId, isGuest, dispatch } = this.props

    if (isGuest) this.handleSaveGuestArea(selectedTownId)
    else this.handleSaveArea(selectedTownId, dispatch)
  }

  // The bus stops step reads the town back from the URL and loads its services.
  private handleSaveGuestArea(id: number | null): void {
    if (!id) return

    this.props.history.replace(`${urls.newReservationBusStopsStep}?townId=${id}`)
  }

  private async handleSaveArea(id: number | null, dispatch: Dispatch): Promise<void> {
    if (!id) return

    try {
      await UserService.updateTown(id)
      const user = await UserService.getInfo()
      dispatch(setUserInfoAction(user))
      dispatch(fetchClientInfoAction())
      dispatch(setAreaChangedAction())
      dispatch(
        setNotificationAction({
          isError: false,
          label: LiteralsService.get('areaChanged', true)
        })
      )
    } catch (error) {
      dispatch(
        setNotificationAction({
          isError: true,
          label: LiteralsService.get('serverError', true)
        })
      )
    }
  }
}


const mapStateToProps = ({ configuration, userInfo }: IStoreState) => ({
  towns: configuration.areas,
  selectedTownId: configuration.selectedAreaId,
  isExperienced: userInfo.experienced,
  userTownId: userInfo.town.id,
  isLoading: userInfo.isLoadingUserInfo,
  isFetchingAreas: configuration.isFetchingAreas,
  documentationRequired: userInfo.documentationRequired,
  isGuest: userInfo.isGuest
})

export default withRouter(connectRedux(mapStateToProps, TownsModal))
